import React, { useEffect, useState } from "react";
import axios from "axios";
import WorkWithUs from "../components/sections/WorkWithUs";
import BookRideSection from "../components/sections/BookRideSection";
import ReviewCard from "../components/common/ReviewCard";
import WriteReviewModal from "../components/common/modals/WriteReviewModal";
import Meta from "../components/common/Meta";

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({ total: 0, average: 0 });
  const [writeModalOpen, setWriteModalOpen] = useState(false);

  const loadReviews = async (page = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_APP_DOMAIN}/api/customer/reviews?page=${page}&per_page=9`
      );
      const payload = res.data.reviews;
      const list = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : [];

      setReviews(list);
      setCurrentPage(payload?.current_page || page);
      setLastPage(payload?.last_page || 1);
      setSummary({
        total: Number(res.data.total ?? list.length) || 0,
        average: Number(res.data.average_rating ?? 0) || 0,
      });
    } catch (err) {
      console.error(err);
      setReviews([]);
      setLastPage(1);
    } finally {
      setLoading(false);
    }
  };

  const handlePageClick = (page) => {
    if (page < 1 || page > lastPage) return;
    loadReviews(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    loadReviews(1);
  }, []);

  useEffect(() => {
    const refresh = () => loadReviews(currentPage);
    window.addEventListener("orbi:review-submitted", refresh);
    return () => window.removeEventListener("orbi:review-submitted", refresh);
  }, [currentPage]);

  const renderPagination = () => {
    const pages = [];

    pages.push(
      <button
        key="prev"
        disabled={currentPage <= 1}
        onClick={() => handlePageClick(currentPage - 1)}
        className="pg-btn"
      >
        ❮
      </button>
    );

    for (let i = 1; i <= lastPage; i++) {
      if (i === 1 || i === lastPage || (i >= currentPage - 1 && i <= currentPage + 1)) {
        pages.push(
          <button
            key={i}
            onClick={() => handlePageClick(i)}
            className={`pg-btn ${i === currentPage ? "active" : ""}`}
          >
            {i}
          </button>
        );
      } else if (
        (i === currentPage - 2 && i > 1) ||
        (i === currentPage + 2 && i < lastPage)
      ) {
        pages.push(
          <span key={`dots-${i}`} className="pg-dots">
            ...
          </span>
        );
      }
    }

    pages.push(
      <button
        key="next"
        disabled={currentPage >= lastPage}
        onClick={() => handlePageClick(currentPage + 1)}
        className="pg-btn"
      >
        ❯
      </button>
    );

    return pages;
  };

  const reviewsJsonLd =
    summary.total > 0
      ? {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "Orbi Cabs",
          url: "https://orbicabs.com",
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: summary.average,
            reviewCount: summary.total,
            bestRating: 5,
            worstRating: 1,
          },
          review: reviews.slice(0, 20).map((item) => ({
            "@type": "Review",
            author: { "@type": "Person", name: item.name || "Customer" },
            datePublished: item.formatted_date || undefined,
            reviewBody: item.review || "",
            reviewRating: {
              "@type": "Rating",
              ratingValue: Number(item.rating) || 5,
              bestRating: 5,
              worstRating: 1,
            },
          })),
        }
      : null;

  return (
    <>
      <Meta
        title="Customer Reviews | Orbi"
        description="Read customer reviews for Orbi airport transfers, city tours and outstation trips. Real ratings from travellers in Chandigarh, Mohali & Panchkula."
        keywords="orbi reviews, cab reviews, airport taxi reviews, chandigarh cab reviews"
        canonical="https://orbicabs.com/reviews"
        jsonLd={reviewsJsonLd}
      />
      <section className="reviews_page_wrap section_pb section_pt">
        <div className="container">
          <div className="row g-4">
            <div className="col-12">
              <div className="reviews_page_header">
                <h2>Customer Reviews</h2>
                <div className="reviews_page_header_right">
                  {summary.total > 0 && (
                    <p className="reviews_page_summary">
                      {summary.average.toFixed(1)} ★ | {summary.total}{" "}
                      {summary.total === 1 ? "Review" : "Reviews"}
                    </p>
                  )}
                  <button
                    type="button"
                    className="reviews_page_write_btn"
                    onClick={() => setWriteModalOpen(true)}
                  >
                    Write Your Review
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-4">
            {loading ? (
              <div className="col-12 text-center py-5">
                <h4>Loading reviews...</h4>
              </div>
            ) : reviews.length === 0 ? (
              <div className="col-12 text-center py-5">
                <h4>No reviews available right now.</h4>
              </div>
            ) : (
              reviews.map((item) => (
                <div key={item.id} className="col-lg-4 col-md-6 col-sm-6 col-12">
                  <div className="review_slide_card reviews_page_card">
                    <ReviewCard item={item} />
                  </div>
                </div>
              ))
            )}
          </div>

          {lastPage > 1 && (
            <div id="pagination-container" className="pagination-wrapper mt-4">
              {renderPagination()}
            </div>
          )}
        </div>
      </section>

      <WorkWithUs />
      <BookRideSection />

      <WriteReviewModal
        open={writeModalOpen}
        onClose={() => setWriteModalOpen(false)}
        onSuccess={() => loadReviews(1)}
      />
    </>
  );
};

export default Reviews;
